import { useEffect, useMemo, useState } from "react";

import FigureQcm from "./FigureQcm";
import Maths from "./Maths";
import { api, type Capacite, type Classe, type QuestionBanque } from "./api";
import {
  abreger,
  chargerReferentiel,
  grouperParTheme,
  SANS_CAPACITE,
  TOUTES_CAPACITES,
} from "./capacites";

// Une compétence évaluée par moins de trois questions n'atteint pas ses
// quatre niveaux (cf. `competences_qcm.QUESTIONS_PAR_COMPETENCE`). La
// composition est l'endroit où ce manque se répare encore.
const QUESTIONS_PAR_COMPETENCE = 3;

const NIVEAUX: Record<string, string> = {
  seconde: "Seconde",
  "1spe": "1re spécialité",
  tspe: "Terminale spécialité",
};

export default function ComposerQcm({
  surComposition,
  surAbandon,
}: {
  surComposition: () => void;
  surAbandon: () => void;
}) {
  const [banque, setBanque] = useState<QuestionBanque[] | null>(null);
  const [classes, setClasses] = useState<Classe[]>([]);
  const [classeId, setClasseId] = useState<number | null>(null);
  const [titre, setTitre] = useState("");
  const [date, setDate] = useState("");
  const [versions, setVersions] = useState(4);
  const [choisies, setChoisies] = useState<string[]>([]);
  const [filtre, setFiltre] = useState("");
  const [niveauFiltre, setNiveauFiltre] = useState("tous");
  const [capaciteFiltre, setCapaciteFiltre] = useState<string>(TOUTES_CAPACITES);
  const [referentiel, setReferentiel] = useState<Map<string, Capacite>>(new Map());
  const [erreur, setErreur] = useState<string | null>(null);
  const [encours, setEncours] = useState(false);

  useEffect(() => {
    api.lireBanque().then(setBanque);
    api.listerClasses().then((liste) => {
      setClasses(liste);
      if (liste.length > 0) setClasseId(liste[0].id);
    });
  }, []);

  // Les niveaux présents dans la banque, et eux seuls : une banque de
  // seconde ne télécharge pas les programmes de première et de terminale.
  useEffect(() => {
    if (!banque) return;
    const niveaux = [...new Set(banque.map((q) => q.niveau))];
    chargerReferentiel(niveaux).then(setReferentiel);
  }, [banque]);

  const parRef = useMemo(
    () => new Map((banque ?? []).map((q) => [q.identifiant, q])),
    [banque],
  );
  const classe = classes.find((c) => c.id === classeId);

  // Le filtre par capacité s'applique en dernier, et les effectifs annoncés
  // dans son menu se comptent sur l'ensemble qui le précède : choisir une
  // capacité ne doit pas changer les nombres qui ont servi à la choisir.
  const avantCapacite = useMemo(() => {
    const cherche = filtre.trim().toLowerCase();
    return (banque ?? []).filter(
      (q) =>
        // Sortie de la circulation veut dire sortie : c'est tout l'objet
        // du retrait, et la composition est l'écran qu'il vise.
        q.retiree_le === null &&
        !choisies.includes(q.identifiant) &&
        (niveauFiltre === "tous" || q.niveau === niveauFiltre) &&
        (cherche === "" ||
          q.identifiant.toLowerCase().includes(cherche) ||
          q.enonce.toLowerCase().includes(cherche)),
    );
  }, [banque, choisies, filtre, niveauFiltre]);

  // Le menu des capacités se compte sur la BANQUE du niveau, pas sur le
  // résultat de la recherche en cours, et les questions déjà choisies y
  // restent comptées. L'effectif annoncé répond à « en ai-je assez sur cette
  // capacité ? », qui est une propriété de la banque : le faire dépendre du
  // mot qu'on vient de taper le viderait de sens, et verrait le menu se
  // rétrécir sous les doigts.
  const duNiveau = useMemo(
    () =>
      (banque ?? []).filter(
        (q) =>
          q.retiree_le === null &&
          (niveauFiltre === "tous" || q.niveau === niveauFiltre),
      ),
    [banque, niveauFiltre],
  );

  const groupes = useMemo(
    () => grouperParTheme(duNiveau, referentiel),
    [duNiveau, referentiel],
  );
  const sansCapacite = duNiveau.filter((q) => q.capacites.length === 0).length;

  // Changer de niveau peut faire disparaître la capacité retenue — les codes
  // en portent le préfixe. La garder afficherait une liste vide sans dire
  // pourquoi, et un menu dont la valeur choisie ne figure plus.
  useEffect(() => {
    if (capaciteFiltre === TOUTES_CAPACITES) return;
    const existe =
      capaciteFiltre === SANS_CAPACITE
        ? sansCapacite > 0
        : groupes.some((g) => g.capacites.some((c) => c.code === capaciteFiltre));
    if (!existe) setCapaciteFiltre(TOUTES_CAPACITES);
  }, [capaciteFiltre, groupes, sansCapacite]);

  const retenue = groupes
    .flatMap((g) => g.capacites)
    .find((c) => c.code === capaciteFiltre);

  const disponibles = useMemo(
    () =>
      avantCapacite.filter((q) =>
        capaciteFiltre === TOUTES_CAPACITES
          ? true
          : capaciteFiltre === SANS_CAPACITE
            ? q.capacites.length === 0
            : q.capacites.includes(capaciteFiltre),
      ),
    [avantCapacite, capaciteFiltre],
  );

  const deplacer = (rang: number, pas: number) =>
    setChoisies((precedent) => {
      const suivant = [...precedent];
      const cible = rang + pas;
      if (cible < 0 || cible >= suivant.length) return precedent;
      [suivant[rang], suivant[cible]] = [suivant[cible], suivant[rang]];
      return suivant;
    });

  const composer = async () => {
    if (classeId === null) return;
    setEncours(true);
    setErreur(null);
    try {
      await api.composerQcm({
        classe_id: classeId,
        identifiants: choisies,
        titre: titre || undefined,
        date_passation: date || undefined,
        versions,
      });
      surComposition();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEncours(false);
    }
  };

  if (!banque) return <p>Chargement…</p>;

  if (classes.length === 0) {
    return (
      <p className="aide">
        Créez d'abord une classe dans l'onglet <strong>Classes</strong> : un QCM
        s'y rattache.
      </p>
    );
  }

  const niveaux = [...new Set(banque.map((q) => q.niveau))];
  // Elles repartent avec leur QCM corrigé : la question sort de la salle.
  // Resservie à une autre classe elle garde sa valeur, à la même dans
  // l'année elle la perd — l'avertissement se voit, il ne bloque pas.
  const dejaVues = choisies.filter((ref) =>
    classe ? parRef.get(ref)?.classes_vues.includes(classe.code) : false,
  );

  return (
    <section className="composer">
      <header className="saisie-entete">
        <div>
          <h2>Composer un QCM</h2>
          <p className="sous-titre">
            {choisies.length} question(s) choisie(s) — l'ordre ci-dessous est
            celui du sujet imprimé
          </p>
        </div>
        <div className="actions">
          <button
            type="button"
            onClick={composer}
            disabled={encours || choisies.length === 0 || classeId === null}
          >
            {encours ? "Composition…" : "Composer le QCM"}
          </button>
          <button type="button" onClick={surAbandon}>
            Abandonner
          </button>
        </div>
      </header>

      <div className="actions">
        <label>
          Classe{" "}
          <select
            value={classeId ?? ""}
            onChange={(e) => setClasseId(Number(e.target.value))}
          >
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.annee_scolaire}
              </option>
            ))}
          </select>
        </label>
        <label>
          Titre{" "}
          <input
            value={titre}
            placeholder="QCM de rentrée"
            onChange={(e) => setTitre(e.target.value)}
          />
        </label>
        <label>
          Date{" "}
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label>
          Versions{" "}
          <select
            value={versions}
            onChange={(e) => setVersions(Number(e.target.value))}
          >
            {[2, 3, 4, 6, 8].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {erreur && <p className="erreur">{erreur}</p>}

      {dejaVues.length > 0 && (
        <div className="anomalies avertissements">
          <strong>
            {dejaVues.length} question(s) que {classe?.code} a déjà vues
          </strong>
          <p className="aide">
            Les élèves repartent avec leur QCM corrigé : la question est sortie
            de la salle. Resservie à une autre classe elle garde sa valeur ; à
            celle-ci, elle mesure un progrès plutôt qu'une acquisition — ce qui
            peut être exactement ce que vous cherchez.
          </p>
        </div>
      )}

      <h3>Le sujet</h3>
      {choisies.length === 0 && (
        <p className="aide">
          Aucune question choisie. Prenez-les dans la banque ci-dessous.
        </p>
      )}
      <table className="questions-qcm">
        <tbody>
          {choisies.map((ref, rang) => {
            const question = parRef.get(ref);
            const vue = classe && question?.classes_vues.includes(classe.code);
            return (
              <tr key={ref}>
                <th>{rang + 1}</th>
                <td>
                  <div className="enonce-qcm">
                    <Maths texte={question?.enonce ?? ref} syntaxe="typst" />
                  </div>
                  <FigureQcm
                    identifiant={ref}
                    presente={question?.a_une_figure}
                  />
                  <div className="fichier">
                    {ref}
                    {question && question.capacites.length > 0 &&
                      ` · ${question.capacites.join(", ")}`}
                    {vue && (
                      <span className="attente"> · déjà vue par {classe.code}</span>
                    )}
                  </div>
                </td>
                <td className="nombre">
                  <button
                    type="button"
                    className="puce"
                    onClick={() => deplacer(rang, -1)}
                    disabled={rang === 0}
                    aria-label="Monter"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="puce"
                    onClick={() => deplacer(rang, 1)}
                    disabled={rang === choisies.length - 1}
                    aria-label="Descendre"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="puce"
                    onClick={() =>
                      setChoisies(choisies.filter((x) => x !== ref))
                    }
                    aria-label="Retirer"
                  >
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h3>La banque</h3>
      <div className="actions">
        <input
          type="search"
          placeholder="Chercher un identifiant, un énoncé…"
          value={filtre}
          onChange={(e) => setFiltre(e.target.value)}
        />
        <select
          value={niveauFiltre}
          onChange={(e) => setNiveauFiltre(e.target.value)}
        >
          <option value="tous">Tous les niveaux</option>
          {niveaux.map((code) => (
            <option key={code} value={code}>
              {NIVEAUX[code] ?? code}
            </option>
          ))}
        </select>
        <select
          className="filtre-capacite"
          value={capaciteFiltre}
          onChange={(e) => setCapaciteFiltre(e.target.value)}
          aria-label="Filtrer par capacité"
        >
          <option value={TOUTES_CAPACITES}>Toutes les capacités</option>
          {sansCapacite > 0 && (
            <option value={SANS_CAPACITE}>
              Sans capacité ({sansCapacite})
            </option>
          )}
          {groupes.map((groupe) => (
            <optgroup key={groupe.theme} label={groupe.theme}>
              {groupe.capacites.map((capacite) => (
                <option key={capacite.code} value={capacite.code}>
                  {capacite.code} — {abreger(capacite.libelle)} ({capacite.nombre})
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {retenue && (
        <p className="aide">
          <strong>{retenue.code}</strong> — {retenue.libelle}
          {retenue.nombre < QUESTIONS_PAR_COMPETENCE && (
            <span className="attente">
              {" "}
              · {retenue.nombre} question{retenue.nombre > 1 ? "s" : ""} en
              banque : il en faut {QUESTIONS_PAR_COMPETENCE} pour qu'une
              compétence atteigne ses quatre niveaux de maîtrise.
            </span>
          )}
        </p>
      )}
      {capaciteFiltre === SANS_CAPACITE && (
        <p className="aide">
          Questions versées sans capacité : elles n'apparaissent sous aucun
          thème, et rien ne les rattache au suivi longitudinal.
        </p>
      )}

      <table className="questions-qcm">
        <tbody>
          {disponibles.map((question) => {
            const vue =
              classe && question.classes_vues.includes(classe.code);
            return (
              <tr key={question.id}>
                <th>
                  <button
                    type="button"
                    className="puce"
                    onClick={() =>
                      setChoisies([...choisies, question.identifiant])
                    }
                    aria-label={`Ajouter ${question.identifiant}`}
                  >
                    +
                  </button>
                </th>
                <td>
                  <div className="enonce-qcm">
                    <Maths texte={question.enonce} syntaxe="typst" />
                  </div>
                  <FigureQcm
                    identifiant={question.identifiant}
                    presente={question.a_une_figure}
                  />
                  <div className="fichier">
                    {NIVEAUX[question.niveau] ?? question.niveau} ·{" "}
                    {question.identifiant}
                    {question.capacites.length > 0 &&
                      ` · ${question.capacites.join(", ")}`}
                    {vue && (
                      <span className="attente"> · déjà vue par {classe.code}</span>
                    )}
                  </div>
                </td>
                <td className="nombre">
                  {question.reussite === null ? (
                    <span className="attente">jamais servie</span>
                  ) : (
                    `${Math.round(question.reussite * 100)} %`
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
